<script lang="ts" setup>
import {useCartStore} from "@/stores/cart";
import {price} from "@/filters/price";
import CartItemRow from "@/components/cart/CartItemRow.vue";
import {computed} from "vue";
import {useModalsStore} from "@/stores/modals";
import {useUserStore} from "@/stores/user";

export type TProduct = {
  id: number,
  title: string,
  url: string,
  sku: string,
  price?: string
}

const modalStore = useModalsStore()
const props = defineProps<{ products: TProduct[] }>()
const cartStore = useCartStore()
const userStore = useUserStore()

const ew = computed(() => {
  return window.user;
})

const product = (productId: string) => props.products.find((product: TProduct) => +product.id === +productId)
const isPriceColumnShown = computed(() => props.products.some((product: TProduct) => product.price))
</script>

<template>
  <template v-if="cartStore.isEmpty">
    <div class="p-5 pt-[30px]">
      <div class="grid place-items-center min-h-[49.938rem] font-myriadPro">
        <div class="flex flex-col items-center gap-2.5">
          <h3 class="text-[38px] desktop:text-[2.5rem] leading-[90%] text-center">Вы не добавили ни одного товара</h3>
          <p class="text-[18px] desktop:text-xl leading-none text-center">Для оформления заказа добавьте товар в корзину</p>
          <a class="max-desktop:uppercase max-desktop:w-full inline-flex desktop:mt-2.5 items-center rounded-[3px] transition-colors disabled:pointer-events-none disabled:opacity-50 bg-[hsl(var(--roke-text-blue))] text-white justify-center text-lg font-semibold h-14 px-5 hover:bg-white hover:text-[hsl(var(--roke-text-blue))]" href="/catalog">
            <span class="mt-0.5">Перейти в каталог</span>
          </a>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <div class="px-2.5 pb-2.5 desktop:p-5 grid grid-cols-1 max-desktop:pt-[100px]">
      <h1 class="text-[50px] leading-none desktop:text-[105px] desktop:leading-[94.5px] font-centuryGothic font-bold uppercase">Корзина</h1>
      <div class="desktop:grid grid-cols-4 gap-[1.25rem]">
        <table class="table-auto border-collapse desktop:border text-xl col-span-3 mt-2.5 desktop:mt-[30px] font-myriadPro h-fit">
          <thead class="max-desktop:hidden">
          <tr class="bg-[hsl(var(--roke-fill-grey))] leading-[90%]">
            <th class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]" colspan="2">Товар</th>
            <th class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]">Описание</th>
            <th class="border font-normal h-[53px] text-[20px] leading-[18px]">Комментарий</th>
            <th v-if="isPriceColumnShown" class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]">Цена</th>
            <th class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]">Количество</th>
            <th v-if="isPriceColumnShown" class="border px-5 font-normal h-[53px] text-[20px] leading-[18px]">Итого</th>
          </tr>
          </thead>
          <tbody class="text-center text-2xl max-desktop:flex flex-col gap-2.5">
          <CartItemRow v-for="(item, n) in cartStore.items" :key="n" :product="product(item[0]) as TProduct"/>
          </tbody>
        </table>
        <div class="grid grid-cols-1 gap-2.5 mt-2.5 desktop:mt-[1.875rem] font-myriadPro h-fit">
          <div class="flex flex-col border p-5 gap-2.5">
            <div v-if="cartStore.totalSum" class="mb-2.5 desktop:mb-5">
              <div class="flex justify-between">
                <h4 class="font-semibold !text-[30px] !desktop:text-[38px] leading-none">Итого</h4>
                <h4 class="font-semibold !text-[30px] !desktop:text-[38px] leading-none">{{ price(cartStore.totalSum) }}</h4>
              </div>
              <div class="flex justify-between text-[20px] desktop:text-2xl">
                <span>В том числе НДС (20%)</span><span>{{ price(cartStore.tax) }}</span>
              </div>
            </div>
            <a v-if="userStore.isAuth" class="inline-flex items-center whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-[hsl(var(--roke-text-blue))] text-white hover:bg-[hsl(var(--roke-text-blue))]/80 w-full leading-[90%] uppercase min-h-[3.438rem]" href="/order">
              <span class="text-[20px] leading-[18px] m-auto font-semibold translate-y-0.5">оформить заказ</span>
            </a>
            <a v-else class="inline-flex items-center whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-secondary text-secondary-foreground hover:bg-secondary/80 w-full opacity-50 cursor-not-allowed leading-[90%] uppercase min-h-[3.438rem]" disabled="" href="#">
              <span class="text-[20px] leading-[18px] m-auto font-semibold translate-y-0.5">необходима авторизация</span>
            </a>
            <button class="mt-2.5 desktop:mt-5 text-[hsl(var(--roke-text-blue))] self-start text-base dekstop:text-[18px] underline underline-offset-4 cursor-pointer" type="button" @click="cartStore.clearCart">Очистить корзину</button>
          </div>
          <div v-if="!userStore.isAuth" class="border p-5 text-[20px] desktop:text-[24px] leading-none text-left">
            <span>Для управления заказами необходима </span>
            <button class="text-[hsl(var(--roke-text-blue))] inline-block" type="button" @click="modalStore.showLoginModal">авторизация</button>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>